import React, {
	Component,
	type ComponentType,
	type ElementRef,
	type ReactNode,
	type MouseEvent,
	type TransitionEvent,
} from 'react';
import noop from 'lodash/noop';
import { Provider as FieldHeightAnimatorProvider } from '../../../utils/height-animator-context/index.tsx';

type Props = {
	isExpanded: boolean;
	Animation: ComponentType<{
		height: number;
		isExpanded: boolean;
		isAnimating: boolean;
		children: ReactNode;
		onMouseDown?: undefined | ((event: MouseEvent<HTMLDivElement>) => void);
		onTransitionEnd?: undefined | ((event: TransitionEvent<HTMLDivElement>) => void);
	}>;
	children: ReactNode;
	onMouseDown: undefined | ((event: MouseEvent<HTMLDivElement>) => void);
	onTransitionEnd: (event: TransitionEvent<HTMLDivElement>) => void;
};

type State = {
	height: number;
	isAnimating: boolean;
};

// eslint-disable-next-line jira/react/no-class-components
export class HeightAnimator extends Component<Props, State> {
	static defaultProps: {
		onMouseDown: Props['onMouseDown'];
		onTransitionEnd: Props['onTransitionEnd'];
	} = {
		onMouseDown: noop,
		onTransitionEnd: noop,
	};

	state: State = {
		height: 0,
		isAnimating: false,
	};

	componentDidUpdate(prevProps: Props) {
		this.onRecalculateAnimatorHeight();

		// Set isAnimating to true whenever isExpanded changes so we can hide overflow and ensure children are visible
		if (this.props.isExpanded !== prevProps.isExpanded) {
			this.setState({ isAnimating: true });
		}
	}

	onRecalculateAnimatorHeight: () => void = () => {
		this.onContainerUpdate(this.containerEl);
	};

	onContainerUpdate: (container: null | HTMLDivElement) => void = (
		container: null | HTMLDivElement,
	) => {
		if (!container) {
			return;
		}

		const height = container.clientHeight;
		this.containerEl = container;
		const { height: currentHeight } = this.state;
		if (currentHeight !== height) {
			this.setState({ height });
		}
	};

	onTransitionEnd = (e: TransitionEvent<HTMLDivElement>): void => {
		this.props.onTransitionEnd(e);
		// Set isAnimating to false whenever transition has completed so we can show overflow and hide children if collapsed.
		this.setState({ isAnimating: false });
	};

	containerEl: ElementRef<'div'> | null = null;

	render() {
		const { children, isExpanded, Animation, onMouseDown } = this.props;

		const { height, isAnimating } = this.state;

		// Render the children off-screen to calculate their size before animating.
		return (
			<Animation
				height={height}
				isExpanded={isExpanded}
				isAnimating={isAnimating}
				onMouseDown={onMouseDown}
				onTransitionEnd={this.onTransitionEnd}
			>
				<div ref={this.onContainerUpdate}>
					<FieldHeightAnimatorProvider value={this.onRecalculateAnimatorHeight}>
						{/* If the container is collapsed and not currently animating then do not render children. */}
						{!isExpanded && !isAnimating ? null : children}
					</FieldHeightAnimatorProvider>
				</div>
			</Animation>
		);
	}
}
